import { getMoscowTime } from "./time.utils";
import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { ZodIssue, z } from "zod";

export interface RequestResponse<T = any> {
  status: string;
  data: T;
  details?: string;
}

/**
 * Утилиты для логирования HTTP запросов и ошибок
 */

/**
 * Маскирует чувствительные данные в заголовках
 */
export function maskSensitiveHeader(key: string, value: any): string {
  const lowerKey = key.toLowerCase();
  if (lowerKey === "authorization" || lowerKey === "telegram-token") {
    return value ? `${String(value).substring(0, 20)}...` : "";
  }
  return String(value);
}

/**
 * Форматирует данные для логирования
 */
function formatDataPreview(data: any, maxLength: number = 200): string {
  if (!data) return "";
  
  if (data instanceof FormData) {
    return "[FormData]";
  }
  
  try {
    const dataStr = typeof data === "string" ? data : JSON.stringify(data);
    return dataStr.length > maxLength ? `${dataStr.substring(0, maxLength)}...` : dataStr;
  } catch {
    return String(data).substring(0, maxLength);
  }
}

/**
 * Логирует ошибку HTTP запроса
 */
export function logHttpError(
  error: AxiosError,
  originalRequest?: AxiosRequestConfig
): void {
  const method = (originalRequest?.method?.toUpperCase() || "GET") as string;
  const fullUrl = originalRequest?.baseURL
    ? `${originalRequest.baseURL}${originalRequest.url}`
    : originalRequest?.url || "";

  // Формируем информацию о запросе
  let sentInfo = "";
  if (originalRequest) {
    const hasData = !!originalRequest.data;
    const dataType = originalRequest.data instanceof FormData ? "FormData" : typeof originalRequest.data;
    const dataPreview = formatDataPreview(originalRequest.data, 200);
    sentInfo = `Отправили: ${method} ${fullUrl}${hasData ? `, данные: ${dataType} (${dataPreview})` : ""}`;
  }

  // Формируем информацию об ответе
  let receivedInfo = "";
  if (error.response) {
    const dataPreview = formatDataPreview(error.response.data, 200);
    receivedInfo = `Получили: статус ${error.response.status} ${error.response.statusText}, данные: ${typeof error.response.data} (${dataPreview})`;
  } else {
    const isNetworkError = error.code === "ERR_NETWORK" || error.message === "Network Error";
    receivedInfo = `Получили: нет ответа (${error.message || error.code}), ${isNetworkError ? "возможна проблема с CORS или сетью" : ""}`;
  }

  console.error(`[${getMoscowTime()}] [RestClient] ✗ Ошибка запроса | ${sentInfo} | ${receivedInfo}`);
}

/**
 * Логирует предупреждение о HTML ответе вместо JSON
 */
export function logHtmlResponseWarning(
  response: AxiosResponse,
  requestInfo: { method: string; fullUrl: string }
): void {
  const responseData = response.data as string;
  const contentType = response.headers["content-type"] || "unknown";
  
  const possibleReasons = [
    "Неправильный URL - сервер возвращает страницу ошибки",
    "Проблема с CORS - браузер блокирует запрос",
    "Сервер не найден (404) - возвращается HTML страница",
    "Проблема с роутингом на сервере"
  ].join("; ");
  
  console.error(
    `[${getMoscowTime()}] [RestClient] ⚠️ Сервер вернул HTML вместо JSON | ` +
    `Запрос: ${requestInfo.method} ${requestInfo.fullUrl} | ` +
    `Статус: ${response.status} ${response.statusText} | ` +
    `Content-Type: ${contentType} | ` +
    `Длина: ${responseData.length} символов | ` +
    `Начало: "${responseData.substring(0, 200)}..." | ` +
    `Возможные причины: ${possibleReasons}`
  );
}

/**
 * Извлекает структуру из Zod схемы для логирования
 */
function extractSchemaStructure(schema: z.ZodType<any>): any {
  try {
    // Для объектов используем shape
    if (schema instanceof z.ZodObject) {
      const shape = schema.shape;
      const result: Record<string, any> = {};
      Object.keys(shape).forEach(key => {
        const fieldSchema = shape[key];
        if (fieldSchema instanceof z.ZodArray) {
          // Для массива извлекаем тип элементов
          const elementType = (fieldSchema as any)._def.type;
          if (elementType instanceof z.ZodObject) {
            result[key] = [extractSchemaStructure(elementType)];
          } else {
            result[key] = [];
          }
        } else if (fieldSchema instanceof z.ZodObject) {
          result[key] = extractSchemaStructure(fieldSchema);
        } else if (fieldSchema instanceof z.ZodString) {
          result[key] = "string";
        } else if (fieldSchema instanceof z.ZodNumber) {
          result[key] = "number";
        } else if (fieldSchema instanceof z.ZodBoolean) {
          result[key] = "boolean";
        } else if (fieldSchema instanceof z.ZodOptional) {
          result[key] = extractSchemaStructure(fieldSchema._def.innerType) + " (optional)";
        } else {
          result[key] = "?";
        }
      });
      return result;
    }
    
    // Для массивов
    if (schema instanceof z.ZodArray) {
      const elementType = (schema as any)._def.type;
      if (elementType instanceof z.ZodObject) {
        return [extractSchemaStructure(elementType)];
      }
      return [];
    }
    
    // Для других типов
    if (schema instanceof z.ZodString) return "string";
    if (schema instanceof z.ZodNumber) return "number";
    if (schema instanceof z.ZodBoolean) return "boolean";
    if (schema instanceof z.ZodOptional) {
      return extractSchemaStructure(schema._def.innerType) + " (optional)";
    }
    
    return "unknown";
  } catch (e) {
    return schema;
  }
}

/**
 * Логирует ошибку валидации схемы ответа
 */
export function logSchemaValidationError(
  response: RequestResponse,
  schemaErrors: ZodIssue[],
  requestInfo: { method: string; fullUrl: string } | null,
  schema?: z.ZodType<any>
): void {
  const requestInfoStr = requestInfo 
    ? `${requestInfo.method} ${requestInfo.fullUrl}`
    : "неизвестный запрос";

  // Извлекаем структуру схемы
  let expectedSchema = null;
  if (schema) {
    try {
      // Если схема обернута в CommonDto.withStatus (имеет поля status, data, details)
      if (schema instanceof z.ZodObject && schema.shape) {
        const hasStatus = schema.shape.status;
        const hasData = schema.shape.data;
        const hasDetails = schema.shape.details;
        
        // Это CommonDto.withStatus - извлекаем data схему
        if (hasStatus && hasData) {
          expectedSchema = {
            status: "string",
            data: extractSchemaStructure(hasData as z.ZodType<any>),
            ...(hasDetails ? { details: "string (optional)" } : {})
          };
        } else {
          // Обычная схема
          expectedSchema = extractSchemaStructure(schema);
        }
      } else {
        expectedSchema = extractSchemaStructure(schema);
      }
    } catch (e) {
      expectedSchema = schema;
    }
  }

  console.error(
    `[${getMoscowTime()}] [RestClient] ✗ Ошибка валидации схемы`,
    {
      запрос: requestInfoStr,
      ожидаласьСхема: expectedSchema || schema,
      пришло: response?.data,
      полныйResponse: response,
    }
  );
}

/**
 * Логирует ошибку в interceptor
 */
export function logInterceptorError(error: any, context: string = "request"): void {
  console.error(`[${getMoscowTime()}] [RestClient] Ошибка в ${context} interceptor:`, error);
}

/**
 * Логирует ошибку фоновой переавторизации
 */
export function logBackgroundReauthError(error: any): void {
  console.error(`[${getMoscowTime()}] [RestClient] Ошибка фоновой переавторизации:`, error);
}

